import { AbstractCase } from "@/src/types/Entities/AbstractCase";
import { Typography } from "@mui/material";

const defaultAuthorizationItems = [
  "außergerichtlichen Interessensvertretung,",
  "Prozessführung einschließlich Einlegung und Rücknahme von Rechtsmitteln,",
  "Entgegennahme von Zustellungen,",
  "Empfangnahme von Geldern,",
  "Begründung und Aufhebung von Vertragsverhältnissen und",
  "Abgabe von einseitigen Willenserklärungen, insbesondere zum Ausspruch von ordentlichen und außerordentlichen Kündigungen.",
];

const defaultOverviewSteps = [
  "Sie erteilen uns die Vollmacht, damit wir Sie vertreten dürfen.",
  "Wir prüfen Ihre rechtlichen Möglichkeiten und melden uns dann umgehend bei Ihnen zurück.",
  "Wir setzen uns mit der Gegenseite in Verbindung und versuchen Ihren Fall außergerichtlich zu klären.",
  "Sollte eine außergerichtliche Klärung nicht möglich sein, vertreten wir Sie selbstverständlich auch im gerichtlichen Verfahren.",
];

export function getPowerOfAttorneyOverviewSteps(product: AbstractCase): string[] {
  if (product.productCategory === "administrativeLaw") {
    return [
      "<strong>Vollmacht erteilen:</strong> Sie unterschreiben digital die Vollmacht, damit wir Sie gegenüber Ihrem Dienstherrn offiziell vertreten dürfen.",
      "<strong>Rechtslage prüfen:</strong> Wir analysieren Ihren Fall und Ihre Erfolgsaussichten und melden uns umgehend bei Ihnen.",
      "<strong>Behörde kontaktieren:</strong> Wir übernehmen die komplette Korrespondenz mit Ihrer Dienststelle und setzen uns für eine außergerichtliche Lösung (z. B. im Widerspruchsverfahren) ein.",
      "<strong>Rechte durchsetzen:</strong> Sollte keine Einigung erzielt werden, vertreten wir Sie selbstverständlich auch vor dem Verwaltungsgericht.",
    ];
  }
  switch (product.name) {
    case "dataLeakContract":
      return [
        defaultOverviewSteps[0],
        "Wir fordern für Sie einen Daten-Auszug bei den Auskunfteien an und stellen Deckungsanfrage bei Ihrer Versicherung.",
        "Nachdem ein Datenauszug vorliegt, prüfen wir Ihre rechtliche Grundlage.",
        "Wenn es zu einer unrechtmäßigen Datenweitergabe kam, setzen wir uns umgehend mit der Gegenseite in Verbindung und versuchen, Ihren Fall zunächst außergerichtlich zu klären.",
        defaultOverviewSteps[3],
      ];
    case "employmentContract":
      return [
        defaultOverviewSteps[0],
        defaultOverviewSteps[1],
        "Wenn keine offenen Fragen mehr zu klären sind, setzen wir uns mit Ihrem Arbeitgeber in Verbindung und versuchen Ihren Fall außergerichtlich zu klären.",
        defaultOverviewSteps[3],
      ];
    default:
      return defaultOverviewSteps;
  }
}

export function getPowerOfAttorneyAuthorizationItems(product: AbstractCase): string[] {
  if (product.productCategory === "administrativeLaw") {
    return [
      "umfassenden Vertretung gegenüber dem Dienstherrn im Verwaltungs- und Vorverfahren (gemäß § 14 VwVfG, § 126 BBG sowie landesrechtlichen Vorschriften),",
      "Prozessführung in gerichtlichen Verfahren (gemäß §§ 67 VwGO, 81 ff. ZPO), einschließlich der Erhebung und Rücknahme von Klagen und Widerklagen,",
      "Vertretung im einstweiligen Rechtsschutz (§§ 80, 123 VwGO),",
      "Bewirkung und Entgegennahme von Zustellungen jeder Art,",
      "Einlegung, Rücknahme von sowie Verzicht auf Rechtsmittel sowie Erledigung des Rechtsstreits durch Vergleich, Verzicht oder Anerkenntnis,",
      "Entgegennahme von Geld, Wertsachen und Urkunden, insbesondere Kostenerstattungen (unter Befreiung von den Beschränkungen des § 181 BGB) und",
      "Einsichtnahme in die Personalakte (einschließlich Nebenakten) sowie Behörden- und Gerichtsakten.",
    ];
  }

  switch (product.name) {
    case "dataLeakContract":
      return [
        "Geltendmachung von umfassenden datenschutzrechtlichen Auskunftsansprüchen gem. Art. 15 DSGVO - insbesondere die Einholung der Auskunft und auch der Erhalt derselben,",
        ...defaultAuthorizationItems,
      ];
    case "employmentContract":
      return [
        "Zur Führung von Prozessen nach der Zivilprozessordnung und dem Arbeitsgerichtsgesetz; zu allen den Rechtsstreit betreffenden Prozesshandlungen, insbesondere zur Erhebung der Widerklage, zur Vornahme und Entgegennahme von Zustellungen.",
        "Zur Vertretung in sonstigen Verfahren und außergerichtlichen Verhandlungen aller Art;",
        "Zur Beseitigung des Rechtsstreits durch Vergleich, Verzicht und Anerkenntnis.",
        "Zur Einlegung und Rücknahme von Rechtsmitteln und zum Verzicht auf solche.",
        "Zur Vertretung in Insolvenzverfahren einschließlich der Befugnis, Forderungen anzumelden und zu bestreiten, sowie eine Insolvenzquote in Empfang zu nehmen;",
        "Zur Begründung und Aufhebung von Vertragsverhältnissen und zur Abgabe und Entgegennahme einseitiger Willenserklärungen, insbesondere Kündigungen (ordentliche wie außerordentliche), Ausübung von Wahlrechten, Rücktritt, Anfechtung und Widerruf;\n" +
          "einschließlich der Befugnis zur Erledigung des Rechtsstreits oder außergerichtliche Verhandlungen durch Vergleich oder Vertrag; die Vollmacht erstreckt sich auf Neben- und Folgeverfahren aller Art und umfasst allgemein die Befugnis:",
        "Zur Prozessführung u. a. nach §§ 81 ff. ZPO.",
        "Zur Bestellung eines Vertreters durch Übertragung der Vollmacht.",
        "Zur Empfangnahme von Geldern und Wertsachen, insbesondere des Streitgegenstandes und der vom Gegner, der Justizkasse oder anderen Stellen zu erstattenden Kosten.",
        "Zu allen Nebenverfahren, z. B. Arrest und einstweilige Verfügung, Kostenfestsetzung, Zwangsvollstreckung einschließlich der aus ihr erwachsenden besonderen Verfahren (z. B. ZPO §§ 726-732, 766-774, 785, 805, 872 ff u.a.).",
        "Zur Einsichtnahme und Vervielfältigung von Akten und Dokumenten sowie der Erhebung, Verarbeitung und Nutzung von Daten aller Art,",
        "Zur Befragung von Personen, insbesondere Amtsträger, Sachbearbeiter und Zeugen;",
      ];
    default:
      return defaultAuthorizationItems;
  }
}

export function getPowerOfAttorneyPreamble(product: AbstractCase, usePlural: boolean) {
  if (product.productCategory === "administrativeLaw") {
    return (
      <>
        <Typography sx={{
          marginBottom: "16px"
        }}>
          Hiermit {usePlural ? "erteilen wir" : "erteile ich"} der Legalbird Rechtsanwälte Rechtsanwalts GmbH Vollmacht {usePlural ? "zu unserer" : "zu meiner"}{" "}
          Vertretung im Verwaltungsverfahren, im Vorverfahren sowie im gerichtlichen Verfahren durch alle Instanzen, mit der Ermächtigung zur Bestellung von
          Unterbevollmächtigten.
        </Typography>
        <Typography sx={{
          marginBottom: "16px"
        }}>Die Vollmacht ermächtigt zu allen die Angelegenheit betreffenden Handlungen, insbesondere zur:</Typography>
      </>
    );
  }

  if (product.name === "employmentContract") {
    return (
      <>
        <Typography sx={{
          marginBottom: "16px"
        }}>
          Hiermit {usePlural ? "erteilen wir" : "erteile ich"} den Legalbird Rechtsanwälten (Legalbird Rechtsanwälte Rechtsanwalts GmbH){" "}
          {usePlural ? "in unseren" : "in meiner"} arbeitsrechtlichen Streitigkeit die Vollmacht zur außergerichtlichen Vertretung aller Art und die
          Prozessvollmacht für alle Verfahren in allen Instanzen.
        </Typography>
        <Typography sx={{
          marginBottom: "16px"
        }}>Diese Vollmacht ermächtigt insbesondere</Typography>
      </>
    );
  }

  return (
    <>
      <Typography sx={{
        marginBottom: "16px"
      }}>
        Hiermit {usePlural ? "erteilen wir" : "erteile ich"} der Legalbird Rechtsanwälte Rechtsanwalts GmbH Vollmacht zu {usePlural ? "unserer" : "meiner"}{" "}
        Verteidigung und Vertretung, mit der Ermächtigung zur Bestellung von Unterbevollmächtigten.
      </Typography>
      <Typography sx={{
        marginBottom: "16px"
      }}>Die Vollmacht ermächtigt zu allen die Angelegenheit betreffenden Handlungen, insbesondere zur</Typography>
    </>
  );
}

export function getPowerOfAttorneyAwarenessText(product: AbstractCase, usePlural: boolean) {
  if (product.name === "employmentContract") {
    return (
      <Typography sx={{
        marginBottom: "16px"
      }}>
        {usePlural ? "Wir bestätigen" : "Ich bestätige"}, von den Anwälten der Legalbird Rechtsanwälte Rechtsanwalts GmbH vor der Mandatserteilung den Hinweis
                erhalten zu haben, dass {usePlural ? "wir" : "ich"}in dem Rechtsstreit vor dem Arbeitsgericht in der ersten Instanz keinen Kostenerstattungsanspruch
                gegen die andere Prozesspartei {usePlural ? "haben" : "habe"}. Die durch die Tätigkeit der Legalbird Rechtsanwälte Rechtsanwalts GmbH entstandenen
                Gebühren und Auslagen erster Instanz tragen {usePlural ? "wir" : "ich"}in jedem Fall selbst. Dies gilt auch bei einer außergerichtlichen Erledigung der
                Angelegenheit. Eine Kostenerstattung ist – je nach Ausgang des Verfahrens – erst ab der zweiten Instanz möglich.
              </Typography>
    );
  }

  return (
    <>
      <Typography sx={{
        marginBottom: "16px"
      }}>
        {product.productCategory === "administrativeLaw"
          ? "Die Vollmacht erstreckt sich auch auf Nebenverfahren (z. B. Kostenfestsetzung, Vollstreckung, Wiederaufnahme)."
          : "Die Vollmacht erstreckt sich auch auf Nebenverfahren mit Ausnahme der Zwangsvollstreckung."}
      </Typography>
      <Typography sx={{
        marginBottom: "16px"
      }}>
        {usePlural ? "Uns" : "Mir"} ist bekannt, dass mit der Vollmacht eine Beauftragung zu {usePlural ? "unserer" : "meiner"} Verteidigung und damit eine
        Anwaltsvergütung entsteht, deren Höhe sich nach den gesetzlichen Vorschriften richtet.
      </Typography>
    </>
  );
}
